import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  TextInput,
  Button,
  Stack,
  Text,
  Alert,
  Divider,
} from '@mantine/core';
import { IconBrandGoogle, IconLock, IconMail } from '@tabler/icons-react';
import { useAuth } from '@/context/AuthContext';

export default function AdminLoginPage() {
  const { login, loginWithGoogle, user, isAdmin: isAdminUser, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already logged in as admin
  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (loading) {
      return;
    }

    // If user is logged in and is admin, redirect to dashboard
    if (user && isAdminUser) {
      navigate('/xyz');
      return;
    }

    // If user is logged in but NOT admin, show error
    // Note: We don't auto-logout here to avoid race conditions
    // The user can manually logout if needed
    if (user && !isAdminUser) {
      setError('You are not authorized to access the admin panel. Please contact an administrator.');
    }
  }, [user, isAdminUser, loading, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setIsSubmitting(false);
      return;
    }

    try {
      await login(email, password);
      setIsSubmitting(false);
      // Navigation will happen via useEffect when admin status is confirmed
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await loginWithGoogle();
      setIsSubmitting(false);
      // Navigation will happen via useEffect when admin status is confirmed
    } catch (err: any) {
      console.error('Google login error:', err);
      setError(err.message || 'Failed to login with Google');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" radius="md" withBorder>
          <Text ta="center">Loading...</Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="md">
          <div>
            <Title order={1} ta="center" mb="xs">
              Admin Login
            </Title>
            <Text c="dimmed" ta="center" size="sm">
              Sign in to access the admin panel
            </Text>
          </div>

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          <form onSubmit={handleEmailLogin}>
            <Stack gap="md">
              <TextInput
                label="Email"
                placeholder="admin@example.com"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftSection={<IconMail size={16} />}
                disabled={isSubmitting}
              />

              <TextInput
                label="Password"
                placeholder="Enter your password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftSection={<IconLock size={16} />}
                disabled={isSubmitting}
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Sign In
              </Button>
            </Stack>
          </form>

          <Divider label="OR" labelPosition="center" />

          <Button
            variant="light"
            fullWidth
            size="md"
            leftSection={<IconBrandGoogle size={16} />}
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
          >
            Sign in with Google
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Only authorized administrators can access this panel
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
}
